import { useEffect, useId, useRef, type MouseEvent, type ReactElement, type ReactNode } from 'react'
import { NavigationMenu as BaseNavigationMenu } from '@base-ui/react/navigation-menu'
import { Link, type To } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import './navigation-menu.css'

type LinkTarget = { to: To; href?: never } | { href: string; to?: never }

export type NavigationLink = LinkTarget & {
  label: string
  /** One short line under the label. Shown for links inside a group. */
  description?: string
  /** A Lucide icon before the label, in a group or on the bar. Decorative: the label names the link. */
  icon?: ReactElement
  /** This link leads to the page being shown: it gets `aria-current="page"` and the selected fill. */
  current?: boolean
}

export type NavigationGroup = {
  /** Names the trigger that opens the group. */
  label: string
  links: readonly NavigationLink[]
}

export type NavigationMenuItem = NavigationLink | NavigationGroup

/** Called on every click of a link, with the click and the link. `event.preventDefault()` keeps the page where it is. */
type OnNavigate = (event: MouseEvent<HTMLAnchorElement>, link: NavigationLink) => void

export type NavigationMenuProps = {
  /** Required. Names the navigation landmark, such as “Main”. */
  label: string
  /** Links, and groups of links that open from a trigger. */
  items: readonly NavigationMenuItem[]
  /** `horizontal` is a bar whose groups open below their trigger. `vertical` stacks everything for a drawer, with each
   *  group's links listed under its label. */
  orientation?: 'horizontal' | 'vertical'
  /** `inverse` sets the bar in light text for dark photography. */
  tone?: 'default' | 'inverse'
  /** Horizontal only: the bar keeps one row and scrolls sideways when it runs out of room, such as a section menu on a
   *  phone. The current link is scrolled into view (only the row scrolls). Groups still open below their trigger. */
  scrollable?: boolean
  /** Called on every click of a link, with the click and the link: for example to close the drawer holding a vertical
   *  menu, or to hold leaving. It fires for modifier and middle clicks too, so callers filter those.
   *  `event.preventDefault()` stays on the page; an open group closes either way. */
  onNavigate?: OnNavigate
  className?: string
}

const isGroup = (item: NavigationMenuItem): item is NavigationGroup => 'links' in item

function LinkContent({ link, detailed }: { link: NavigationLink; detailed: boolean }) {
  if (!detailed) {
    return (
      <>
        {link.icon && <span className="play-navigation__icon" aria-hidden="true">{link.icon}</span>}
        {link.label}
      </>
    )
  }
  return (
    <>
      {link.icon && <span className="play-navigation__icon" aria-hidden="true">{link.icon}</span>}
      <span className="play-navigation__text">
        <span className="play-navigation__label">{link.label}</span>
        {link.description && <span className="play-navigation__description">{link.description}</span>}
      </span>
    </>
  )
}

/** A link rendered by Base UI, so the bar's arrow keys reach it. Clicking it closes an open group (`closeOnClick`; Base
 *  UI leaves groups open by default), also when `onNavigate` keeps the page where it is. */
function MenuLink({ link, detailed = false, onNavigate }: { link: NavigationLink; detailed?: boolean; onNavigate?: OnNavigate }) {
  const target = link.to !== undefined ? { render: <Link to={link.to} /> } : { href: link.href }
  return (
    <BaseNavigationMenu.Link
      active={link.current}
      closeOnClick
      onClick={onNavigate && ((event) => onNavigate(event, link))}
      className={cn('play-navigation__link', detailed && 'play-navigation__link--detailed')}
      {...target}
    >
      <LinkContent link={link} detailed={detailed} />
    </BaseNavigationMenu.Link>
  )
}

/** A plain link for the vertical menu, which has no popups to coordinate. */
function PlainLink({ link, detailed = false, onNavigate }: { link: NavigationLink; detailed?: boolean; onNavigate?: OnNavigate }) {
  const props = {
    className: cn('play-navigation__link', detailed && 'play-navigation__link--detailed'),
    'aria-current': link.current ? ('page' as const) : undefined,
    'data-active': link.current ? '' : undefined,
    onClick: onNavigate && ((event: MouseEvent<HTMLAnchorElement>) => onNavigate(event, link)),
    children: <LinkContent link={link} detailed={detailed} />,
  }
  return link.to !== undefined ? <Link to={link.to} {...props} /> : <a href={link.href} {...props} />
}

/** A group in the vertical menu: its label stays visible and names the list of its links. */
function VerticalGroup({ group, onNavigate }: { group: NavigationGroup; onNavigate?: OnNavigate }) {
  const id = useId()
  return (
    <>
      <p id={id} className="play-navigation__group-label">{group.label}</p>
      <ul className="play-navigation__links" aria-labelledby={id}>
        {group.links.map((link) => <li key={link.label}><PlainLink link={link} detailed onNavigate={onNavigate} /></li>)}
      </ul>
    </>
  )
}

function Root({ label, tone, orientation, scrollable = false, className, children }: {
  label: string; tone: 'default' | 'inverse'; orientation: 'horizontal' | 'vertical'; scrollable?: boolean; className?: string; children: ReactNode
}) {
  const classes = cn(
    'play-navigation', `play-navigation--${orientation}`, tone === 'inverse' && 'play-navigation--inverse',
    scrollable && orientation === 'horizontal' && 'play-navigation--scrollable', className,
  )
  if (orientation === 'vertical') return <nav aria-label={label} className={classes}>{children}</nav>
  return <BaseNavigationMenu.Root aria-label={label} className={classes}>{children}</BaseNavigationMenu.Root>
}

/** Brings the current link of a scrolling row into view, centred where the row allows, whenever the current page
 *  changes. Only the row scrolls, never the page. */
function useCurrentInView(enabled: boolean, current: string | undefined) {
  const list = useRef<HTMLUListElement>(null)
  useEffect(() => {
    const row = list.current
    const link = row?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!enabled || !row || !link || row.clientWidth === 0) return
    const rowBox = row.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    row.scrollLeft += linkBox.left - rowBox.left - (rowBox.width - linkBox.width) / 2
  }, [enabled, current])
  return list
}

/** Site navigation: a bar of links where a group, such as Docs, opens a panel of links with a line each. Hover or
 *  Enter opens a group; arrow keys move along the bar and Escape closes it. Use `orientation="vertical"` in the drawer
 *  that replaces the bar on small screens, and `scrollable` for one row that scrolls sideways, such as the Admin
 *  sections. */
export function NavigationMenu({ label, items, orientation = 'horizontal', tone = 'default', scrollable = false, onNavigate, className }: NavigationMenuProps) {
  const current = items.flatMap((item) => (isGroup(item) ? item.links : [item])).find((link) => link.current)?.label
  const list = useCurrentInView(scrollable && orientation === 'horizontal', current)
  if (orientation === 'vertical') {
    return (
      <Root label={label} tone={tone} orientation={orientation} className={className}>
        <ul className="play-navigation__list">
          {items.map((item) => (
            <li key={item.label} className="play-navigation__item">
              {isGroup(item) ? (
                <VerticalGroup group={item} onNavigate={onNavigate} />
              ) : (
                <PlainLink link={item} onNavigate={onNavigate} />
              )}
            </li>
          ))}
        </ul>
      </Root>
    )
  }

  return (
    <Root label={label} tone={tone} orientation={orientation} scrollable={scrollable} className={className}>
      <BaseNavigationMenu.List ref={list} className="play-navigation__list">
        {items.map((item) => (
          <BaseNavigationMenu.Item key={item.label} className="play-navigation__item">
            {isGroup(item) ? (
              <>
                <BaseNavigationMenu.Trigger className="play-navigation__trigger" data-current={item.links.some((link) => link.current) || undefined}>
                  {item.label}
                  <BaseNavigationMenu.Icon className="play-navigation__chevron"><ChevronDown aria-hidden="true" /></BaseNavigationMenu.Icon>
                </BaseNavigationMenu.Trigger>
                <BaseNavigationMenu.Content className="play-navigation__content">
                  <ul className="play-navigation__links">
                    {item.links.map((link) => <li key={link.label}><MenuLink link={link} detailed onNavigate={onNavigate} /></li>)}
                  </ul>
                </BaseNavigationMenu.Content>
              </>
            ) : (
              <MenuLink link={item} onNavigate={onNavigate} />
            )}
          </BaseNavigationMenu.Item>
        ))}
      </BaseNavigationMenu.List>
      <BaseNavigationMenu.Portal>
        <BaseNavigationMenu.Positioner sideOffset={6} collisionPadding={12} align="start" className="play-navigation__positioner">
          <BaseNavigationMenu.Popup className="play-theme play-navigation__popup">
            <BaseNavigationMenu.Viewport className="play-navigation__viewport" />
          </BaseNavigationMenu.Popup>
        </BaseNavigationMenu.Positioner>
      </BaseNavigationMenu.Portal>
    </Root>
  )
}
